import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    eyebrow: string
    title: ReactNode
    description?: ReactNode
    align?: 'left' | 'center'
    tone?: 'light' | 'dark'
    titleId?: string
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    align = 'left',
    tone = 'light',
    titleId,
    className
}): ReactNode => {
    const isDark = tone === 'dark'

    return (
        <div className={cn('reveal flex flex-col gap-5', align === 'center' && 'mx-auto items-center text-center', className)}>
            <p
                className={cn(
                    'inline-flex items-center gap-3 text-[0.72rem] font-semibold uppercase tracking-[0.3em]',
                    isDark ? 'text-brand-gold' : 'text-brand-ember'
                )}
            >
                <span aria-hidden='true' className={cn('h-px w-8', isDark ? 'bg-brand-gold/70' : 'bg-brand-gold')} />
                {eyebrow}
                {align === 'center' && <span aria-hidden='true' className={cn('h-px w-8', isDark ? 'bg-brand-gold/70' : 'bg-brand-gold')} />}
            </p>

            <h2
                id={titleId}
                className={cn(
                    'font-heading text-[2.6rem] leading-[1.02] font-semibold tracking-tight sm:text-5xl lg:text-[3.6rem]',
                    isDark ? 'text-brand-white' : 'text-brand-charcoal'
                )}
            >
                {title}
            </h2>

            {description && (
                <div
                    className={cn(
                        'max-w-2xl text-base leading-relaxed sm:text-[1.05rem]',
                        isDark ? 'text-brand-mist' : 'text-brand-stone'
                    )}
                >
                    {description}
                </div>
            )}
        </div>
    )
}
